import { Skeleton } from '@mui/material';
import type { JSX } from 'react';

import { Text } from 'Components/Text/Text';
import { RiskHistory, RiskResponse, formatTotalImpactOrProbability } from 'Models/RiskRegister';

import styles from './RiskCallout.module.css';

interface RiskCalloutProps {
    calloutType: 'total_inherent_risk' | 'control_environment_effectiveness' | 'total_current_risk' | 'total_target_risk';
    completeEnvironmentEffectivenessAverage?: number;
    error?: Error;
    risks?: RiskResponse[] | RiskHistory[];
}

export const RiskCallout = (props: RiskCalloutProps): JSX.Element => {
    const getRiskStats = (risks: RiskResponse[] | RiskHistory[], riskType: keyof RiskResponse): { total?: number; average?: number } => {
        const definedValues = risks
            .map((risk) => {
                switch (riskType) {
                    case 'total_target_risk':
                        return risk.total_target_risk ?? risk.total_current_risk;
                    default:
                        return risk[riskType];
                }
            })
            .filter((value) => value !== undefined);
        const total = definedValues.length ? (definedValues as number[]).reduce((a, b) => a + b, 0) : undefined;
        const average = total !== undefined && definedValues.length ? total / definedValues.length : undefined;
        return { total: riskType === 'control_environment_effectiveness' ? props.completeEnvironmentEffectivenessAverage : total, average };
    };

    if (props.error) {
        return (
            <Text variant="Header4" color="lightGray">
                {props.error.message}
            </Text>
        );
    } else if (props.completeEnvironmentEffectivenessAverage === undefined || props.risks === undefined) {
        return (
            <div className={styles.innerAverageCell} data-testid={props.calloutType}>
                <div className={styles.stat}>
                    <Skeleton>
                        <Text noStyles variant="Header4" color="darkGray">
                            {props.calloutType === 'control_environment_effectiveness' ? 'RISK-RELATED' : 'AVERAGE'}
                        </Text>
                        <Text noStyles variant="Text1" color="white">
                            -
                        </Text>
                    </Skeleton>
                </div>
                <div className={styles.stat}>
                    <Skeleton>
                        <Text noStyles variant="Header4" color="darkGray">
                            {props.calloutType === 'control_environment_effectiveness' ? 'OVERALL' : 'TOTAL'}
                        </Text>
                        <Text noStyles variant="Text1" color="white">
                            -
                        </Text>
                    </Skeleton>
                </div>
            </div>
        );
    } else {
        return (
            <div className={styles.innerAverageCell} data-testid={props.calloutType}>
                <div className={styles.stat}>
                    <Text noStyles variant="Header4" color="darkGray">
                        {props.calloutType === 'control_environment_effectiveness' ? 'RISK-RELATED' : 'AVERAGE'}
                    </Text>
                    <Text noStyles variant="Text1" color="white">
                        {formatTotalImpactOrProbability(getRiskStats(props.risks, props.calloutType).average)}
                    </Text>
                </div>
                <div className={styles.stat}>
                    <Text noStyles variant="Header4" color="darkGray">
                        {props.calloutType === 'control_environment_effectiveness' ? 'OVERALL' : 'TOTAL'}
                    </Text>
                    <Text noStyles variant="Text1" color="white">
                        {formatTotalImpactOrProbability(getRiskStats(props.risks, props.calloutType).total)}
                    </Text>
                </div>
            </div>
        );
    }
};
